import {Checkbox, Input} from 'antd';
import React, {FC} from 'react';

import {DateInput} from 'components/@common/inputs/date-input';
import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';
import {IReportConfigurationQueryParameterDto, TReportConfigurationQueryParameterVariableType} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {ETableReportFilterFieldType} from 'store/slices/table-report-slice/table-report-slice-types';

import {TableReportDateRangeInput} from '../../../fields';
import {formatReferenceDataForSqlValueSet} from '../utils/table-report-filter.utils';

export interface ITableReportFieldResolverProps {
    label: string;
    type: TReportConfigurationQueryParameterVariableType;
    format?: string;

    // Этот блок должен быть передан от Form. Item
    value?: any;
    onChange?: (value: any) => void;

    // Поля для reference select
    referenceUrl?: string;
    referenceUrlQueryParams?: {
        [index: string]: string;
    };
    showSearch?: boolean;
    isClearable?: boolean;

    disabled?: boolean;

    paramName: string;
    areParametersApplied: boolean;
    queryParameter?: IReportConfigurationQueryParameterDto;
}

export const TableReportFieldResolver: FC<ITableReportFieldResolverProps> = ({
    referenceUrl,
    referenceUrlQueryParams,
    showSearch,
    type,
    paramName,
    areParametersApplied,
    disabled,
    ...props
}: ITableReportFieldResolverProps) => {
    const inputPlaceholder = `Введите ${props?.label?.toLowerCase()}`;
    const selectPlaceholder = 'Выберите значение';

    const isDisabled = areParametersApplied || disabled;

    const handleNumberInputChange = (value: string) => {
        props?.onChange?.(value.split(',').join('.'));
    };

    switch (type) {
    case ETableReportFilterFieldType.INTEGER:
    case ETableReportFilterFieldType.NUMBER:
        return (
            // InputNumber ограничивает максимальное значение до Number.MAX_SAFE_INTEGER, чего нам не не достаточно
            <Input
                {...props}
                onChange={e => {
                    e.persist();
                    handleNumberInputChange(e.target.value);
                }}
                value={props.value}
                onBlur={e => {
                    e.persist();
                    let {value} = e.target;
                    if (value.startsWith('.')) value = `0${value}`;
                    handleNumberInputChange(value);
                }}
                disabled={isDisabled}
                autoComplete="off"
                placeholder="Введите значение"
            />
        );
    case ETableReportFilterFieldType.BOOLEAN:
        return (
            <Checkbox
                disabled={isDisabled}
                {...props}
            >
                {props.label}
            </Checkbox>
        );
    case ETableReportFilterFieldType.DATE:
        return (
            <DateInput
                value={props.value}
                onChange={props.onChange}
                disabled={isDisabled}
                resetTimeOnSet
            />
        );
    case ETableReportFilterFieldType.DATETIME:
        return (
            <DateInput
                showTime
                value={props.value}
                onChange={props.onChange}
                disabled={isDisabled}
            />
        );
    case ETableReportFilterFieldType.VALUE_SET:
        return (
            <CustomSelect
                {...props}
                settings={{
                    showSearch,
                    isClearable: props.isClearable,
                    valuePath: 'lookupCode',
                    labelPath: 'meaning',
                    url: referenceUrl,
                    placeholder: selectPlaceholder,
                    referenceUrlQueryParams: referenceUrlQueryParams ?? {},
                    isDisabled,
                }}
                fixedDropdown={false}
            />
        );
    case ETableReportFilterFieldType.SQL_VALUE_SET:
    case ETableReportFilterFieldType.SQL_MULTI_VALUE_SET:
        return (
            <>
                <CustomSelect
                    {...props}
                    settings={{
                        showSearch,
                        mode: type === ETableReportFilterFieldType.SQL_MULTI_VALUE_SET
                            ? ECustomSelectMode.multiple : undefined,
                        isClearable: props.isClearable,
                        valuePath: 'lookupCode',
                        labelPath: 'meaning',
                        url: referenceUrl,
                        placeholder: selectPlaceholder,
                        referenceUrlQueryParams: referenceUrlQueryParams ?? {},
                        isDisabled,
                    }}
                    rawDataInStoreFormatter={data => formatReferenceDataForSqlValueSet(data, paramName)}
                    fixedDropdown={false}
                />
            </>
        );
    case ETableReportFilterFieldType.MULTI_VALUE_SET:
        return (
            <CustomSelect
                {...props}
                settings={{
                    showSearch,
                    mode: ECustomSelectMode.multiple,
                    isClearable: props.isClearable,
                    valuePath: 'lookupCode',
                    labelPath: 'meaning',
                    url: referenceUrl,
                    placeholder: selectPlaceholder,
                    isDisabled,
                }}
                fixedDropdown={false}
            />
        );
    case ETableReportFilterFieldType.VALUERANGE:
        return (
            <TableReportDateRangeInput
                {...props}
                paramName={paramName}
                isDisabled={isDisabled}
            />

        );
    default:
        return (
            <Input
                {...props}
                disabled={isDisabled}
                autoComplete="off"
                placeholder={inputPlaceholder}
            />
        );
    }
};
